import { named, withDependencies } from '@wix/thunderbolt-ioc'
import type { BrowserWindow, BusinessLogger, Experiments, ViewerModel } from '@wix/thunderbolt-symbols'
import {
	BrowserWindowSymbol,
	BusinessLoggerSymbol,
	ExperimentsSymbol,
	MasterPageFeatureConfigSymbol,
	SiteFeatureConfigSymbol,
	ViewerModelSym,
} from '@wix/thunderbolt-symbols'
import { links, isSSR } from '@wix/thunderbolt-commons'
import type { ISessionManager } from 'feature-session-manager'
import { SessionManagerSymbol } from 'feature-session-manager'

import type {
	IAudioPlaybackState,
	IMpaNavigation,
	MpaNavigationMasterPageConfig,
	MpaNavigationSiteConfig,
} from './types'
import { AudioPlaybackStateSymbol, name } from './symbols'

export const MpaNavigation = withDependencies(
	[
		named(SiteFeatureConfigSymbol, name),
		named(MasterPageFeatureConfigSymbol, name),
		ViewerModelSym,
		ExperimentsSymbol,
		BrowserWindowSymbol,
		BusinessLoggerSymbol,
		SessionManagerSymbol,
		AudioPlaybackStateSymbol,
	],
	(
		siteConfig: MpaNavigationSiteConfig,
		masterPageConfig: MpaNavigationMasterPageConfig,
		viewerModel: ViewerModel,
		experiments: Experiments,
		browserWindow: BrowserWindow,
		businessLogger: BusinessLogger,
		sessionManager: ISessionManager,
		audioPlaybackState: IAudioPlaybackState
	): IMpaNavigation => {
		const isLoggedIn = () => !!(sessionManager.getSiteMemberId() || sessionManager.getSmToken())

		return {
			isEligible: ({ anchorCompId, anchorDataId, skipHistory } = {}) => {
				if (siteConfig.forceMpaNavigation) {
					return true
				}

				// In SSR (no browserWindow), we assume view transitions are supported for MPA navigation eligibility
				// since SSR doesn't need to check browser capabilities - the client will handle transitions
				// This may mean there is a mismatch at runtime between the server and client isEligible() results
				const browserSupportsViewTransitions = browserWindow ? 'CSSViewTransitionRule' in browserWindow : false
				const supportsViewTransitions = isSSR(browserWindow) || browserSupportsViewTransitions

				const excludeFeedbackFromMpa =
					experiments['specs.thunderbolt.excludeGetFeedbackFromMpa'] && siteConfig.hasFeedbackQueryParam

				const hasAudioEverPlayed = audioPlaybackState.hasEverPlayed()
				const blockMpaOnAudioPlayback =
					experiments['specs.thunderbolt.blockMpaNavigationOnAudioPlayback'] && hasAudioEverPlayed

				const isEligibleToMpaNavigation =
					process.env.PACKAGE_NAME !== 'thunderbolt-ds' &&
					!!viewerModel.mpaNavigationCompatible &&
					(!masterPageConfig.hasPageTransition || supportsViewTransitions) &&
					!isLoggedIn() &&
					!siteConfig.isRunningInDifferentSiteContext &&
					!anchorCompId &&
					!anchorDataId &&
					!skipHistory &&
					!excludeFeedbackFromMpa &&
					!blockMpaOnAudioPlayback

				void businessLogger.logger.log(
					{
						src: 72,
						evid: 19,
						isEligible: isEligibleToMpaNavigation,
						...(viewerModel.mpaIncompatibleWidgetsList?.length && {
							mpaIncompatibleWidgetsList: viewerModel.mpaIncompatibleWidgetsList.join(','),
						}),
					},
					{ endpoint: 'bolt-performance' }
				)

				return !!experiments['specs.thunderbolt.fullPageNavigationSpecificSites'] && isEligibleToMpaNavigation
			},
			navigate: (href: string) => {
				if (!browserWindow) {
					return false
				}

				const currentUrl = new URL(browserWindow.location.href)
				const navigableUrl = new URL(href)
				navigableUrl.protocol = currentUrl.protocol
				navigableUrl.href = links.appendInternalQueryParamsToHref(navigableUrl.href, currentUrl)

				if (navigableUrl.href === currentUrl.href) {
					return true
				}

				browserWindow.sessionStorage.setItem('isMpa', 'true')
				browserWindow.location.href = navigableUrl.href

				return true
			},
		}
	}
)
